import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './settings.module.scss';
import GeneralTab from './tabs/generalTab/generalTab';
import SecurityTab from './tabs/securityTab/securityTab';
import SessionsTab from './tabs/sessionsTab/sessionsTab';
import VerificationTab from './tabs/verificationTab/verificationTab';

type Tab = 'General' | 'Security' | 'Sessions' | 'Verification';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('General');
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const tabLabels: Record<Tab, string> = {
    General: t('settings.tabs.general'),
    Security: t('settings.tabs.security'),
    Sessions: t('settings.tabs.sessions'),
    Verification: t('settings.tabs.verification'),
  };

  useEffect(() => {
    if (!tabsContainerRef.current) return;
    const activeBtn = tabsContainerRef.current.querySelector(
      `.${styles.active}`,
    ) as HTMLElement | null;
    if (activeBtn) {
      const targetLeft = activeBtn.offsetLeft;
      const targetWidth = activeBtn.offsetWidth;
      const containerWidth = tabsContainerRef.current.offsetWidth;
      const scrollTarget = targetLeft - containerWidth / 2 + targetWidth / 2;
      tabsContainerRef.current.scrollTo({
        left: Math.max(0, scrollTarget),
        behavior: 'smooth',
      });
    }
  }, [activeTab]);

  const handleTabClick = (tab: Tab, e: React.MouseEvent<HTMLButtonElement>) => {
    setActiveTab(tab);
    if (tabsContainerRef.current) {
      const target = e.currentTarget;
      const targetLeft = target.offsetLeft;
      const targetWidth = target.offsetWidth;
      const containerWidth = tabsContainerRef.current.offsetWidth;
      const scrollTarget = targetLeft - containerWidth / 2 + targetWidth / 2;
      tabsContainerRef.current.scrollTo({
        left: Math.max(0, scrollTarget),
        behavior: 'smooth',
      });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'General':
        return <GeneralTab />;
      case 'Security':
        return <SecurityTab />;
      case 'Sessions':
        return <SessionsTab />;
      case 'Verification':
        return <VerificationTab onNavigate={setActiveTab} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.settingsWrapper}>
      <div ref={tabsContainerRef} className={styles.tabsContainer}>
        {(['General', 'Security', 'Sessions', 'Verification'] as Tab[]).map((tab) => (
          <button
            key={tab}
            className={`${styles.tabBtn} ${activeTab === tab ? styles.active : ''}`}
            onClick={(e) => handleTabClick(tab, e)}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      <div className={styles.contentContainer}>{renderTabContent()}</div>
    </div>
  );
};

export default Settings;
